<script lang="ts">
	import { onMount } from 'svelte';

	const playerPromise = import('@mux/mux-player');

	interface Props {
		playbackId: string;
		autoplay?: 'muted' | 'any' | null;
		loop?: boolean;
		muted?: boolean;
		wrapperClass?: string;
		rewindWhenOutOfView?: boolean;
		threshold?: number;
		maxResolution?: '720p' | '1080p' | '1440p' | '2160p';
		controls?: boolean;
		fit?: 'contain' | 'cover';
		videoElement?: HTMLVideoElement;
		onloaded?: () => void;
		class?: string;
		children?: (videoElement: HTMLVideoElement | undefined, loaded: boolean) => any;
	}

	let {
		playbackId,
		autoplay = 'muted',
		loop = true,
		muted = true,
		wrapperClass,
		rewindWhenOutOfView = true,
		threshold = 0,
		maxResolution = '1080p',
		controls = false,
		fit = 'cover',
		videoElement = $bindable(),
		onloaded,
		class: className,
		children
	}: Props = $props();

	let playOnLoad = false;

	let wrapper: HTMLDivElement;

	onMount(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				const { isIntersecting } = entries[0];

				if (!videoElement) {
					playOnLoad = isIntersecting;
					return;
				}

				if (isIntersecting && videoElement.paused) {
					videoElement.play();
				} else if (!videoElement.paused) {
					videoElement.pause();
					if (rewindWhenOutOfView) {
						videoElement.currentTime = 0;
					}
				}
			},
			{ threshold }
		);
		observer.observe(wrapper);

		return () => {
			observer.disconnect();
		};
	});

	let cssVars = $derived(
		`--dialog: none; --media-object-fit: ${fit}; ${controls ? '' : '--controls: none;'}`
	);

	let loaded = $state(false);

	let placeholderSrc = $derived(
		`https://image.mux.com/${playbackId}/thumbnail.jpg?time=0&width=320`
	);
</script>

<div bind:this={wrapper} class="relative{wrapperClass ? ` ${wrapperClass}` : ''}">
	{#if !loaded}
		<img
			alt=""
			class="absolute inset-0 h-full w-full bg-stone-4 object-cover"
			src={placeholderSrc}
		/>
	{/if}
	{#await playerPromise then}
		<!-- svelte-ignore a11y_click_events_have_key_events -->
		<!-- svelte-ignore a11y_no_static_element_interactions -->
		<mux-player
			bind:this={videoElement}
			max-resolution={maxResolution}
			stream-type="on-demand"
			playback-id={playbackId}
			thumbnail-time="0"
			{autoplay}
			{loop}
			{muted}
			accent-color="#00945E"
			prefer-cmcd="false"
			style={cssVars}
			class={className}
			onloadeddata={() => {
				loaded = true;
				onloaded?.();

				if (playOnLoad) {
					videoElement?.play();
				}
			}}
			onclick={(e: MouseEvent) => {
				e.stopPropagation();
			}}
		></mux-player>
	{/await}
	{@render children?.(videoElement, loaded)}
</div>
